// AuthProvider — global auth state for the app.
//
// On mount: read `inkstead_token` from localStorage and verify it against
// /api/auth/me. If the token is invalid (expired, user deleted, etc.) the
// backend returns 401 and we silently drop the token so the user lands as
// signed-out instead of stuck in a broken state.
//
// login/register store the token + user on success.
// `setUser` is exposed for flows that get a session by side-effect (e.g.
// guest-checkout booking, which creates an account + returns {user, token}
// in a single response from /api/bookings/guest).
import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("inkstead_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("inkstead_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const r = await api.post("/auth/login", { email, password });
      localStorage.setItem("inkstead_token", r.data.token);
      setUser(r.data.user);
      return { ok: true };
    } catch (e) { return { ok: false, error: formatApiError(e.response?.data?.detail) }; }
  };
  const register = async (payload) => {
    try {
      const r = await api.post("/auth/register", payload);
      localStorage.setItem("inkstead_token", r.data.token);
      setUser(r.data.user);
      return { ok: true };
    } catch (e) { return { ok: false, error: formatApiError(e.response?.data?.detail) }; }
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("inkstead_token");
    setUser(null);
  };
  const refresh = async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); } catch {}
  };


  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
